/**
 * ProgressiveWizard — TurboTax-style one-question-at-a-time form.
 *
 * Props:
 *   steps: [{ key, title, description?, field, type: "text"|"number"|"select"|"textarea"|"radio",
 *             options?: [{value,label}], placeholder?, required?, min?, max?,
 *             aiHint?: (value, form) => string }]
 *   initialValues: {key: value}
 *   onComplete: (values) => void
 *   onSaveDraft?: (values) => void
 *   aiHelperText?: string
 */
import { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Progress } from "./ui/progress";
import { Card, CardContent } from "./ui/card";
import { ChevronLeft, ChevronRight, Check, Sparkles, Save } from "lucide-react";

export default function ProgressiveWizard({
  steps = [],
  initialValues = {},
  onComplete,
  onSaveDraft,
  title = "Guided Setup",
}) {
  const [idx, setIdx] = useState(0);
  const [values, setValues] = useState(initialValues);

  const step = steps[idx];
  const progress = useMemo(() => Math.round(((idx + 1) / steps.length) * 100), [idx, steps.length]);

  if (!step) return null;

  const current = values[step.field];
  const isValid = step.required
    ? current !== undefined && current !== null && String(current).trim() !== ""
    : true;

  const setField = (val) => setValues((v) => ({ ...v, [step.field]: val }));

  const next = () => {
    if (!isValid) return;
    if (idx < steps.length - 1) setIdx(idx + 1);
    else onComplete?.(values);
  };
  const back = () => idx > 0 && setIdx(idx - 1);

  return (
    <Card className="max-w-2xl mx-auto shadow-lg" data-testid="progressive-wizard">
      <CardContent className="p-8 space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title}</h2>
            <span className="text-xs text-slate-400">
              Step {idx + 1} of {steps.length}
            </span>
          </div>
          <Progress value={progress} className="h-2" data-testid="wizard-progress" />
        </div>

        {/* Step content */}
        <div className="space-y-3">
          <h3 className="text-2xl font-bold text-slate-900" data-testid="wizard-question">
            {step.title}
          </h3>
          {step.description && (
            <p className="text-sm text-slate-600">{step.description}</p>
          )}

          {step.aiHint && (
            <div className="flex items-start gap-2 p-3 bg-sky-50 border border-sky-200 rounded-lg">
              <Sparkles className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
              <p className="text-xs text-sky-800" data-testid="wizard-ai-hint">
                {typeof step.aiHint === "function" ? step.aiHint(current, values) : step.aiHint}
              </p>
            </div>
          )}

          <div className="pt-2">
            <Label className="text-sm font-medium text-slate-700">
              {step.label || step.title}
              {step.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            {step.type === "textarea" ? (
              <Textarea
                value={current || ""}
                onChange={(e) => setField(e.target.value)}
                placeholder={step.placeholder}
                rows={4}
                className="mt-1.5"
                data-testid={`wizard-field-${step.field}`}
                autoFocus
              />
            ) : step.type === "select" ? (
              <Select value={current || ""} onValueChange={setField}>
                <SelectTrigger className="mt-1.5" data-testid={`wizard-field-${step.field}`}>
                  <SelectValue placeholder={step.placeholder || "Select..."} />
                </SelectTrigger>
                <SelectContent>
                  {(step.options || []).map((opt) => (
                    <SelectItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : step.type === "radio" ? (
              <div className="mt-2 space-y-2">
                {(step.options || []).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setField(opt.value)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition ${
                      current === opt.value
                        ? "border-sky-500 bg-sky-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`wizard-option-${opt.value}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{opt.label}</p>
                        {opt.description && (
                          <p className="text-xs text-slate-500 mt-0.5">{opt.description}</p>
                        )}
                      </div>
                      {current === opt.value && <Check className="w-4 h-4 text-sky-600" />}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <Input
                type={step.type === "number" ? "number" : "text"}
                value={current || ""}
                onChange={(e) =>
                  setField(step.type === "number" ? Number(e.target.value) : e.target.value)
                }
                placeholder={step.placeholder}
                min={step.min} max={step.max}
                className="mt-1.5"
                data-testid={`wizard-field-${step.field}`}
                autoFocus
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            variant="ghost" onClick={back} disabled={idx === 0}
            data-testid="wizard-back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            {onSaveDraft && (
              <Button variant="outline" onClick={() => onSaveDraft(values)} data-testid="wizard-save-draft-btn">
                <Save className="w-4 h-4 mr-1" /> Save draft
              </Button>
            )}
            <Button
              onClick={next} disabled={!isValid}
              className="bg-sky-700 hover:bg-sky-800"
              data-testid="wizard-next-btn"
            >
              {idx === steps.length - 1 ? "Finish" : "Next"}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
